

import { useState, useEffect, useRef, useCallback } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
/* Card and Button are gone: both render only Bootstrap class names, and
   this project ships no Bootstrap stylesheet. Row and Col stay -- .row and
   .col-md-* are reimplemented in css/grid.css, so the columns do work. */
import { Col, Row } from "react-bootstrap";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from '../../common/Toast/toast';
import moment from "moment";
import { confirm } from "../../common/Confirm/confirm";
import { RefreshCw } from "lucide-react";

// Custom components
import TextInput from "../../common/FormFields/TextInput.jsx";
import DatePickerInput from "../../common/FormFields/DatePickerInput.jsx";
import NewCustomAsyncSelectDropdown from "../../common/NewCustomAsyncSelectDropdown.jsx";
import AsyncSelectDropdown from "../../common/CustomAsyncDropdown.jsx";
import LabClashNote from "./LabClashNote.jsx";
import usePagedOptions, { hasNextPage } from "../../common/usePagedOptions.js";

// Services
import {
  createBatch,
  updateBatch,
  partialUpdateBatch,
  getBatchById,
} from "../../../../services/BatchService.js";
import { getcourses } from "../../../../services/CourseModule.js";
import { getTrainers } from "../../../../services/TrainerService.js";
import { getAllStudents } from "../../../../services/StudentFromBillingService.js";
import { getClassrooms } from "../../../../services/ClassroomService.js";
import { getBranches } from "../../../../services/BranchService.js";
import { useAuth } from "../../../../context/AuthContext.js";
import { getMe } from "../../../../services/AuthService.js";

/* The page kit (pg, pg-head, pg-btn, pg-icon-btn, pg-notice, pg-spinner)
   and this form's own chrome. */
import "../../Trainer/TrainerDashboard.css";
import "./BatchForm.css";

/**
 * The batch form, used two ways:
 *
 *   - as the /add-batch and /add-batch/:id page, where it takes its id from
 *     the route and navigates away when it is done;
 *   - inside the batches list's popup, where the list passes `batchId`,
 *     `onCancel` and `onSaved` and nothing navigates at all. That is the
 *     same arrangement the events list already uses for AddEventForm.
 *
 * Props are optional, so the page route keeps working untouched.
 */
/* The form in three steps, the way Mark attendance is: five sections in
   one dialog ran past its fixed height, and a --form panel's body does
   not scroll -- its step stage does, and this form had none, so the
   lower sections could not be reached at all. Each step owns the fields
   it validates before Continue lets the reader past it. */
const STEP_LABELS = ["Details", "Meeting & material", "Students"];

// What a batch can be marked as when it is edited. The API accepts these
// four and nothing else (batch.service.js BATCH_STATUSES).
const BATCH_STATUS_OPTIONS = [
  { value: "Ongoing", label: "Ongoing" },
  { value: "End", label: "End" },
  { value: "Dissolved", label: "Dissolved" },
  { value: "Merged", label: "Merged" },
];

// Some branch names are saved with trailing spaces ("Deccan ").
const branchLabel = (b) => {
  const name = String(b?.name || "").trim() || "Unnamed branch";
  return b?.code ? `${name} (${String(b.code).trim()})` : name;
};

// "Classroom not assign yet" holds batches of every branch, so its branch
// is no hint for one batch. Same test as lms-api utils/labClash.js.
const isUnassignedRoom = (room) =>
  /\b(not\s*(yet\s*)?assign|unassign)/i.test(String(room?.classroomNo || ""));

const STEP_FIELDS = [
  ["name", "courseId", "trainerId", "classroom", "startTime", "endTime", "days"],
  ["mode", "meetingLink", "meetingId", "password", "studyMaterialLink", "recordedLectureLink"],
  ["userId"],
];

const AddBatch = ({ batchId, onCancel, onSaved }) => {
  const navigate = useNavigate();
  const { id: routeId } = useParams();
  const id = batchId ?? routeId;
  // Embedded means the dialog around it owns the title and the chrome.
  const embedded = typeof onSaved === 'function' || typeof onCancel === 'function';
  const { user: authUser, hasPermission } = useAuth();

  // ---------- What each picker holds, pinned at the top of its list ----------
  const [selectedCourseObj, setSelectedCourseObj] = useState(null);
  const [selectedTrainerObj, setSelectedTrainerObj] = useState(null);
  const [selectedClassroomObj, setSelectedClassroomObj] = useState(null);
  const [selectedBranchObj, setSelectedBranchObj] = useState(null);
  const [selectedStudentObjs, setSelectedStudentObjs] = useState([]);

  const originalStudentIdsRef = useRef(new Set());

  // ---------- Form state ----------
  const [initialValues, setInitialValues] = useState({
    name: "",
    courseId: "",
    trainerId: "",
    userId: [],
    classroom: "",
    branch: "",
    startTime: "",
    endTime: "",
    mode: "",
    meetingLink: "",
    meetingId: "",
    password: "",
    days: [],
    studyMaterialLink: "",
    recordedLectureLink: "",
    batchStatus: "Ongoing",
    // A note to ADD on save. Always starts empty: the notes already saved
    // are read in the batch view's Batch Notes tab, and the API appends
    // this one with the author and date (it never replaces them).
    newNote: "",
  });

  const [isLoading, setIsLoading] = useState(false);
  // An edit whose batch did not load shows no form: its blank fields and
  // empty student list would be saved over the real ones.
  const [loadFailed, setLoadFailed] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isTrainer, setIsTrainer] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  const isEditMode = Boolean(id);

  // Which step the form is on, and the one painted -- it trails `step`
  // by one exit, as in AddAttendance, so a step leaves before the next
  // arrives instead of swapping in a single frame.
  const [step, setStep] = useState(1);
  // Another running batch holds the picked classroom then (LabClashNote);
  // the API refuses that save, so the save button stays off.
  const [labClash, setLabClash] = useState(false);
  const [shownStep, setShownStep] = useState(1);
  const [stepPhase, setStepPhase] = useState("in");
  const stageRef = useRef(null);

  useEffect(() => {
    if (shownStep === step) return undefined;
    setStepPhase("out");
    const t = setTimeout(() => {
      setShownStep(step);
      setStepPhase("in");
    }, 160);
    return () => clearTimeout(t);
  }, [step, shownStep]);

  // A new step starts at its top, not wherever the last one was left.
  useEffect(() => {
    if (stageRef.current) stageRef.current.scrollTop = 0;
  }, [shownStep]);

  // Continue checks only this step's fields; later steps have not been
  // shown yet, so their errors are not the reader's to fix here.
  const goToNextStep = async ({ validateForm, setTouched, touched }) => {
    const errs = await validateForm();
    const fields = STEP_FIELDS[step - 1];
    setTouched({ ...touched, ...Object.fromEntries(fields.map((f) => [f, true])) });
    if (fields.some((f) => errs[f])) return;
    setStep(step + 1);
  };

  // Saving checks everything, and a failure goes back to the step that
  // holds it -- an error on a step that is not on screen is invisible.
  const submitFromLastStep = async ({ validateForm, setTouched, submitForm }) => {
    const errs = await validateForm();
    const failing = STEP_FIELDS.findIndex((fields) => fields.some((f) => errs[f]));
    if (failing === -1) {
      submitForm();
      return;
    }
    setTouched(Object.fromEntries(STEP_FIELDS.flat().map((f) => [f, true])));
    setStep(failing + 1);
  };
  const hasShownToast = useRef(false);
  const batchFetchedRef = useRef(false);
  const originalBatchDataRef = useRef(null);

  // Day options (static)
  const dayOptions = [
    { value: "monday", label: "Monday" },
    { value: "tuesday", label: "Tuesday" },
    { value: "wednesday", label: "Wednesday" },
    { value: "thursday", label: "Thursday" },
    { value: "friday", label: "Friday" },
    { value: "saturday", label: "Saturday" },
    { value: "sunday", label: "Sunday" },
  ];

  // Fields that trainers can edit (Meeting Information + Study Material sections)
  const trainerEditableFields = [
    // A trainer may leave a note on their batch like anyone who can edit it.
    "newNote",
    "mode",
    "meetingLink",
    "meetingId",
    "password",
    "studyMaterialLink",
    "recordedLectureLink",
  ];

  const isFieldEditable = (fieldName) => {
    if (isAdmin) return true;
    if (isTrainer && isEditMode)
      return trainerEditableFields.includes(fieldName);
    if (!isEditMode && isTrainer) return false; // Trainers cannot create new batches
    return true;
  };

  // Validation schema - only validate fields that are editable based on role
  const getValidationSchema = () => {
    if (isAdmin || !isEditMode) {
      return Yup.object({
        name: Yup.string().required("Batch name is required"),
        courseId: Yup.string().required("Course is required"),
        trainerId: Yup.string().required("Trainer is required"),
        userId: Yup.array().min(1, "At least one student is required"),
        classroom: Yup.string().required("Classroom is required"),
        startTime: Yup.date().required("Start date and time is required"),
        endTime: Yup.date()
          .required("End date and time is required")
          .min(Yup.ref("startTime"), "End date must be after start date"),
        mode: Yup.string().required("Mode is required"),
        days: Yup.array().min(1, "At least one day is required"),
        meetingLink: Yup.string().required("Meeting link is required"),
        meetingId: Yup.string().when("meetingLink", {
          is: (val) => val && val.trim() !== "",
          then: (schema) => schema.required("Meeting ID is required"),
        }),
        password: Yup.string().when("meetingLink", {
          is: (val) => val && val.trim() !== "",
          then: (schema) => schema.required("Password is required"),
        }),
        studyMaterialLink: Yup.string().nullable().optional(),
        recordedLectureLink: Yup.string().nullable().optional(),
      });
    } else {
      // For trainer edit mode - only validate editable fields
      return Yup.object({
        mode: Yup.string().required("Mode is required"),
        meetingLink: Yup.string().required("Meeting link is required"),
        meetingId: Yup.string().when("meetingLink", {
          is: (val) => val && val.trim() !== "",
          then: (schema) => schema.required("Meeting ID is required"),
        }),
        password: Yup.string().when("meetingLink", {
          is: (val) => val && val.trim() !== "",
          then: (schema) => schema.required("Password is required"),
        }),
        studyMaterialLink: Yup.string().nullable().optional(),
        recordedLectureLink: Yup.string().nullable().optional(),
      });
    }
  };

  // ---------- Paged dropdowns ----------
  /* Course, trainer and classroom each page through the API as the menu
     scrolls (usePagedOptions). The batch's own choice is pinned at the
     top on an edit, so the field shows it before any page has loaded. */
  const coursePicker = usePagedOptions(
    async ({ search, page, limit }) => {
      try {
        const res = await getcourses(limit, page, search, { includeTotal: false });
        const rows = res.data || [];
        return {
          options: rows.map((c) => ({
            value: c.id,
            label: `${c.name} (${c.code})`,
            original: c,
          })),
          hasNext: hasNextPage(res.pagination, page, rows.length, limit),
        };
      } catch (err) {
        toast.error("Error loading courses");
        throw err;
      }
    },
    { pinned: [selectedCourseObj] },
  );

  const trainerPicker = usePagedOptions(
    async ({ search, page, limit }) => {
      try {
        const res = await getTrainers(limit, page, search, false);
        const rows = res.data || [];
        return {
          options: rows.map((t) => ({
            value: t.id || "",
            label: t.trainerName
              ? `${t.trainerName} (${t.trainerEmail || "N/A"})`
              : "N/A",
            original: t,
          })),
          hasNext: hasNextPage(res.pagination, page, rows.length, limit),
        };
      } catch (err) {
        toast.error("Error loading trainers");
        throw err;
      }
    },
    { pinned: [selectedTrainerObj] },
  );

  const classroomPicker = usePagedOptions(
    async ({ search, page, limit }) => {
      try {
        const res = await getClassrooms(limit, page, search, false);
        const rows = res.data || [];
        return {
          options: rows.map((r) => ({
            value: r.id,
            label: `${r.classroomNo} (${r.status})`,
            original: r,
          })),
          hasNext: hasNextPage(res.pagination, page, rows.length, limit),
        };
      } catch (err) {
        toast.error("Error loading classrooms");
        throw err;
      }
    },
    { pinned: [selectedClassroomObj] },
  );

  const branchPicker = usePagedOptions(
    async ({ search, page, limit }) => {
      try {
        const res = await getBranches(limit, page, search, false);
        const rows = res.data || [];
        return {
          options: rows.map((b) => ({
            value: b.id,
            label: branchLabel(b),
            original: b,
          })),
          hasNext: hasNextPage(res.pagination, page, rows.length, limit),
        };
      } catch (err) {
        toast.error("Error loading branches");
        throw err;
      }
    },
    { pinned: [selectedBranchObj] },
  );

  /* Students: the react-select picker pages itself when handed a
     loadOptions that takes { search, page, limit } and answers
     { options, hasNext } -- page one on open, the next on scroll, page
     one again per search. Refresh remounts it to start over. */
  const [studentPickerKey, setStudentPickerKey] = useState(0);
  const loadStudentPage = useCallback(async ({ search, page, limit }) => {
    try {
      const res = await getAllStudents(page, limit, search || "", false);
      const students = res.data || [];
      const options = students.map((s) => ({
        value: s.id,
        label: `${s.name || s.studentName} - ${s.email || s.studentEmail} (${s.courseIds?.map((c) => c.name).join(", ") || "No course"})`,
        original: s,
      }));
      return {
        options,
        hasNext: hasNextPage(res.pagination, page, students.length, limit),
      };
    } catch (err) {
      console.error(err);
      toast.error("Error loading students");
      return { options: [], hasNext: false };
    }
  }, []);

  const refreshStudents = () => setStudentPickerKey((k) => k + 1);

  // ---------- Fetch batch data for edit mode ----------
  const fetchBatchData = useCallback(async () => {
    if (batchFetchedRef.current) return;
    batchFetchedRef.current = true;
    try {
      setIsLoading(true);
      const res = await getBatchById(id);
      const batch = res.data;
      originalBatchDataRef.current = batch;

      const formatDateLocal = (dateString) => {
        if (!dateString) return "";
        return moment(dateString).format("YYYY-MM-DD hh:mm A");
      };

      // ---- Course ----
      let courseId = "";
      if (batch.courseId && batch.courseId[0]) {
        const course = batch.courseId[0];
        courseId = course.id;
        const courseOption = {
          value: course.id,
          label: `${course.name} (${course.code})`,
          original: course,
        };
        setSelectedCourseObj(courseOption);
      }

      // ---- Trainer ----
      let trainerId = "";
      if (batch.trainerId && batch.trainerId[0]) {
        const trainer = batch.trainerId[0];
        trainerId = trainer.id;
        const label = trainer.trainerName
          ? `${trainer.trainerName} (${trainer.trainerEmail || ""})`
          : `Trainer ${trainerId.slice(0, 6)}`;
        const trainerOption = { value: trainerId, label, original: trainer };
        setSelectedTrainerObj(trainerOption);
      }

      // ---- Classroom ----
      let classroomId = "";
      if (batch.classroom && batch.classroom[0]) {
        const room = batch.classroom[0];
        classroomId = room.id;
        const classroomOption = {
          value: room.id,
          label: `${room.classroomNo} (${room.status})`,
          original: room,
        };
        setSelectedClassroomObj(classroomOption);
      }

      // ---- Branch ----
      // The batch's own branch (the classroom's when it has none).
      let branchId = "";
      if (batch.branch && batch.branch.id) {
        branchId = batch.branch.id;
        setSelectedBranchObj({
          value: batch.branch.id,
          label: branchLabel(batch.branch),
          original: batch.branch,
        });
      }

      // ---- Students ----
      let selectedStudents = [];
      if (batch.userId && Array.isArray(batch.userId)) {
        selectedStudents = batch.userId.map((student) => {
          const label = `${student.name || student.studentName} - ${student.email || student.studentEmail} (${student.courseName || "No course"})`;
          const studentOption = { value: student.id, label, original: student };
          return studentOption;
        });
        selectedStudents.sort((a, b) => {
          const nameA = a.label.split(" - ")[0].toLowerCase();
          const nameB = b.label.split(" - ")[0].toLowerCase();
          return nameA.localeCompare(nameB);
        });
        setSelectedStudentObjs(selectedStudents);

        originalStudentIdsRef.current = new Set(
          selectedStudents.map((s) => s.value),
        );
      }

      // ---- Days ----
      let selectedDays = [];
      if (batch.days && typeof batch.days === "string") {
        const dayIds = batch.days.split(",");
        selectedDays = dayIds.map((d) => {
          const day = dayOptions.find((opt) => opt.value === d.trim());
          return day || { value: d.trim(), label: `Day: ${d.trim()}` };
        });
      }

      setInitialValues({
        name: batch.name || "",
        courseId,
        trainerId,
        userId: selectedStudents,
        classroom: classroomId,
        branch: branchId,
        startTime: formatDateLocal(batch.startTime),
        endTime: formatDateLocal(batch.endTime),
        mode: batch.mode || "",
        meetingLink: batch.meetingLink || "",
        meetingId: batch.meetingId || "",
        password: batch.password || "",
        days: selectedDays,
        studyMaterialLink: batch.studyMaterialLink || "",
        recordedLectureLink: batch.recordedLectureLink || "",
        // Batches saved before the field existed have none: they are running.
        batchStatus: batch.batchStatus || "Ongoing",
        newNote: "",
      });
    } catch (error) {
      toast.error("Failed to fetch batch data");
      setLoadFailed(true);
    } finally {
      setIsLoading(false);
    }
  }, [id, dayOptions]);

  const retryBatchLoad = () => {
    batchFetchedRef.current = false;
    setLoadFailed(false);
    fetchBatchData();
  };

  // ---------- User role check ----------
  const checkUserRole = useCallback(async () => {
    try {
      const response = await getMe();
      const userData = response.data;
      setCurrentUser(userData);
      const isAdminUser =
        userData.isSuperAdmin ||
        (userData.roles &&
          userData.roles.some((r) => r.name.toLowerCase() === "admin")) ||
        (userData.permissions &&
          userData.permissions.some((p) => p.toLowerCase().includes("admin")));
      const isTrainerUser =
        (userData.roles &&
          userData.roles.some((r) => r.name.toLowerCase() === "trainer")) ||
        (userData.profiles &&
          userData.profiles.some((p) => p.label.toLowerCase() === "trainer"));
      setIsAdmin(isAdminUser);
      setIsTrainer(isTrainerUser);
    } catch (error) {
      toast.error("Error checking user role");
    }
  }, []);

  // ---------- Permissions check ----------
  useEffect(() => {
    if (!hasPermission("batches", "add", "lms") && !isEditMode) {
      if (!hasShownToast.current) {
        toast.error("You do not have permission to add batches.");
        hasShownToast.current = true;
      }
    }
  }, [hasPermission, navigate, isEditMode]);

  useEffect(() => {
    checkUserRole();
  }, [checkUserRole]);

  useEffect(() => {
    if (isEditMode && currentUser && !batchFetchedRef.current && !isLoading) {
      fetchBatchData();
    }
  }, [isEditMode, currentUser, fetchBatchData, isLoading]);

  // ---------- Form submission ----------
  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      setSubmitting(true);

      // For trainer edit mode - use partial update
      if (isEditMode && isTrainer && !isAdmin) {
        // Only send the editable fields
        const partialPayload = {
          mode: values.mode,
          meetingLink: values.meetingLink,
          meetingId: values.meetingId,
          password: values.password,
          studyMaterialLink: values.studyMaterialLink || "",
          recordedLectureLink: values.recordedLectureLink || "",
          newNote: values.newNote || "",
        };

        await partialUpdateBatch(id, partialPayload);
        toast.success("Batch updated successfully!");

        // Embedded, the list closes the dialog and reloads itself; there is
        // nowhere to navigate to and no reason to wait 1.5s to do nothing.
        if (onSaved) onSaved();
        else {
          setTimeout(() => {
            navigate("/trainer-calendar");
          }, 1500);
        }
      }
      // For admin or create mode - use full update/create
      else {
        const currentStudentIds = values.userId.map((s) => s.value).join(",");

        let addUserIds = "";
        let removeUserIds = "";

        if (isEditMode) {
          const currentSet = new Set(values.userId.map((s) => s.value));
          const added = values.userId
            .filter((s) => !originalStudentIdsRef.current.has(s.value))
            .map((s) => s.value);
          addUserIds = added.join(",");

          const removed = [...originalStudentIdsRef.current].filter(
            (id) => !currentSet.has(id),
          );
          removeUserIds = removed.join(",");
        } else {
          addUserIds = values.userId.map((s) => s.value).join(",");
        }

        const payload = {
          ...values,
          trainerId: values.trainerId,
          userId: currentStudentIds,
          startTime: new Date(values.startTime).toISOString(),
          days: values.days.map((d) => d.value).join(","),
          endTime: new Date(values.endTime).toISOString(),
          studyMaterialLink: values.studyMaterialLink || "",
          recordedLectureLink: values.recordedLectureLink || "",
          createdBy: isEditMode ? undefined : authUser?.id || "NA",
          updatedBy: authUser?.id || "NA",
          addUserIds,
          removeUserIds,
        };

        if (isEditMode) {
          await updateBatch(id, payload);
          toast.success("Batch updated successfully!");
        } else {
          await createBatch(payload);
          toast.success("Batch created successfully!");
          resetForm();
        }

        if (onSaved) onSaved();
        else {
          setTimeout(() => {
            if (isTrainer && !isAdmin) {
              navigate("/trainer-dashboard");
            } else {
              navigate("/batches");
            }
          }, 1500);
        }
      }
    } catch (err) {
      // A refusal names its reason (a lab already booked, for one); say it.
      toast.error(err?.response?.data?.message?.message || `Failed to ${isEditMode ? "update" : "create"} batch`);
    } finally {
      setSubmitting(false);
    }
  };

  // Helper function to get disabled styles
  const getDisabledStyles = (isDisabled) => {
    if (isDisabled) {
      return {
        backgroundColor: "#e9ecef",
        cursor: "not-allowed",
        opacity: 0.7,
      };
    }
    return {};
  };

  /* Both guards were `d-flex justify-content-center align-items-center`
     around a `spinner-border` and an `alert alert-warning` -- five Bootstrap
     class names, and this project ships no Bootstrap stylesheet, so the
     spinner drew nothing, the warning was plain body text and neither was
     centred. The page kit has all three. */
  if (isLoading && isEditMode) {
    return (
      <div className="bf-state">
        <span className="pg-spinner" role="status" aria-hidden="true" />
        <p className="pg-state-text">Loading batch...</p>
      </div>
    );
  }

  if (loadFailed && isEditMode) {
    return (
      <div className="bf-state">
        <p className="pg-notice pg-notice--bad bf-notice">
          <span>This batch could not be loaded, so it cannot be edited yet.</span>
        </p>
        <button type="button" className="pg-btn" onClick={retryBatchLoad}>
          Try again
        </button>
      </div>
    );
  }

  // Trainer cannot create new batches
  if (isTrainer && !isAdmin && !isEditMode) {
    return (
      <div className="bf-state">
        <p className="pg-notice pg-notice--bad bf-notice">
          <span>
            Trainers cannot create batches. Only an admin can.
          </span>
        </p>
        {onCancel ? (
          <button type="button" className="pg-btn" onClick={onCancel}>
            Close
          </button>
        ) : (
          <Link to="/trainer-calendar" className="pg-btn">
            Back to calendar
          </Link>
        )}
      </div>
    );
  }

  /* The header card, the container-fluid and the two row/col-12 wrappers
     around a single card each are gone: `container-fluid` does not resolve
     here, and inside the list's dialog the dialog's own head already says
     which batch this is. The page route keeps a header, from the house kit
     rather than an orange inline-styled Card. */
  /* Add mode has a note of its own now that the list opens this in a
     dialog: a trainer who gets here finds the save button disabled by
     `isTrainer && !isAdmin && !isEditMode` below, and without a line
     saying why, a dead button in a popup reads as a broken one. */
  const limitedNote =
    isTrainer && !isAdmin
      ? isEditMode
        ? "Trainer mode — only the meeting and study-material fields can be changed."
        : "Trainer mode — new batches are created by an administrator."
      : null;

  return (
    // --picker gives the Course / Trainer / Classroom lists their scrolling
    // box back: bare att-embed flattens every .nas-options, and a list that
    // cannot scroll never asks for its next page.
    <div className={embedded ? "bf-form bf-form--embedded att-embed att-embed--picker" : "pg bf-page"}>
      {!embedded && (
        <header className="pg-head">
          <div className="pg-head-main">
            <h1 className="pg-title">{isEditMode ? "Edit batch" : "Add batch"}</h1>
            <p className="pg-sub">
              {limitedNote ||
                (isEditMode
                  ? "Update this batch's details, schedule and roster."
                  : "Name the batch, set when it runs and who is in it.")}
            </p>
          </div>
          <div className="pg-head-actions">
            <Link
              to={isTrainer && !isAdmin ? "/trainer-calendar" : "/batches"}
              className="pg-btn"
            >
              Back to {isTrainer && !isAdmin ? "calendar" : "batch list"}
            </Link>
          </div>
        </header>
      )}

      {embedded && limitedNote && (
        <p className="pg-notice bf-notice">
          <span>{limitedNote}</span>
        </p>
      )}

      <div className={embedded ? "bf-body" : "bf-card"}>
              <div className={embedded ? "" : "bf-card-body"}>
                <Formik
                  enableReinitialize
                  initialValues={initialValues}
                  validationSchema={getValidationSchema()}
                  onSubmit={handleSubmit}
                >
                  {({
                    values,
                    setFieldValue,
                    isSubmitting,
                    errors,
                    touched,
                    validateForm,
                    setTouched,
                    submitForm,
                  }) => (
                    <Form className="att-form">
                      <ol className="att-steps">
                        {STEP_LABELS.map((label, i) => {
                          const n = i + 1;
                          const state =
                            n === step ? "is-now" : n < step ? "is-done" : "";
                          return (
                            <li className={`att-step-tab ${state}`.trim()} key={label}>
                              <button
                                type="button"
                                // Adding: only backwards. Editing: every step is
                                // filled, so any tab opens.
                                disabled={n === step || (!isEditMode && n > step)}
                                onClick={() => setStep(n)}
                              >
                                <span className="att-step-n">{n}</span>
                                {label}
                              </button>
                            </li>
                          );
                        })}
                      </ol>

                      {/* The one scrolling surface in the dialog. */}
                      <div className={`att-stage is-${stepPhase}`} ref={stageRef}>
                      <div className="att-stage-inner" key={shownStep}>
                      {shownStep === 1 && (
                      <section className="att-step">
                      {/* ── Basic Information ── Only show for Admin or Create Mode ── */}

                      <div className="bf-section">
                        <h5 className="bf-section-title">
                          Basic Information
                        </h5>
                        {/* Status first, and only when editing: a new batch
                            is Ongoing by definition. Same control as Mode. */}
                        {isEditMode && (
                          <Row>
                            <Col md={6}>
                              <label className="form-label">Batch status</label>
                              <NewCustomAsyncSelectDropdown
                                isMulti={false}
                                options={BATCH_STATUS_OPTIONS}
                                value={
                                  values.batchStatus
                                    ? [{ value: values.batchStatus, label: values.batchStatus }]
                                    : []
                                }
                                onChange={(selectedOptions) => {
                                  const selected =
                                    selectedOptions && selectedOptions.length > 0
                                      ? selectedOptions[0]
                                      : null;
                                  setFieldValue("batchStatus", selected?.value || "Ongoing");
                                }}
                                placeholder="Select status..."
                                isDisabled={!isFieldEditable("batchStatus")}
                                isSearchable={false}
                                styles={{
                                  control: (provided) => ({
                                    ...provided,
                                    ...getDisabledStyles(!isFieldEditable("batchStatus")),
                                    backgroundColor: !isFieldEditable("batchStatus")
                                      ? "#e9ecef"
                                      : provided.backgroundColor,
                                  }),
                                }}
                              />
                            </Col>
                          </Row>
                        )}
                        <Row>
                          <Col md={6}>
                            <TextInput
                              name="name"
                              label="Batch Name *"
                              placeholder="Enter batch name"
                              disabled={!isFieldEditable("name")}
                              style={{
                                borderColor:
                                  touched.name && errors.name
                                    ? "#dc3545"
                                    : "#e5e7eb",
                                borderRadius: "8px",
                                ...getDisabledStyles(!isFieldEditable("name")),
                              }}
                            />
                          </Col>
                          <Col md={6}>
                            <div style={{ height: "100%" }}>
                              <label
                                className="form-label"
                              >
                                Course *
                              </label>
                              <NewCustomAsyncSelectDropdown
                                isMulti={false}
                                options={coursePicker.options}
                                value={
                                  coursePicker.options.find(
                                    (c) => c.value === values.courseId,
                                  )
                                    ? [
                                        coursePicker.options.find(
                                          (c) => c.value === values.courseId,
                                        ),
                                      ]
                                    : []
                                }
                                onChange={(selectedOptions) => {
                                  const selected =
                                    selectedOptions &&
                                    selectedOptions.length > 0
                                      ? selectedOptions[0]
                                      : null;
                                  setFieldValue(
                                    "courseId",
                                    selected?.value || "",
                                  );
                                  setSelectedCourseObj(selected || null);
                                }}
                                placeholder="Select course..."
                                isDisabled={!isFieldEditable("courseId")}
                                isLoading={coursePicker.isLoading}
                                onMenuOpen={coursePicker.onMenuOpen}
                                onInputChange={coursePicker.onInputChange}
                                onMenuScrollToBottom={coursePicker.onMenuScrollToBottom}
                                onRefresh={coursePicker.refresh}
                                loadingMessage="Loading courses..."
                                noOptionsMessage="No courses available"
                                noSearchResultsMessage="No courses found"
                                styles={{
                                  control: (provided) => ({
                                    ...provided,
                                    ...getDisabledStyles(
                                      !isFieldEditable("courseId"),
                                    ),
                                    backgroundColor: !isFieldEditable(
                                      "courseId",
                                    )
                                      ? "#e9ecef"
                                      : provided.backgroundColor,
                                  }),
                                }}
                              />
                              {touched.courseId && errors.courseId && (
                                <div
                                  className="text-danger"
                                >
                                  {errors.courseId}
                                </div>
                              )}
                            </div>
                          </Col>
                        </Row>
                        <Row>
                          <Col md={6}>
                            <div style={{ height: "100%" }}>
                              <label
                                className="form-label"
                              >
                                Trainer *
                              </label>
                              <NewCustomAsyncSelectDropdown
                                isMulti={false}
                                options={trainerPicker.options}
                                value={
                                  trainerPicker.options.find(
                                    (t) => t.value === values.trainerId,
                                  )
                                    ? [
                                        trainerPicker.options.find(
                                          (t) => t.value === values.trainerId,
                                        ),
                                      ]
                                    : []
                                }
                                onChange={(selectedOptions) => {
                                  const selected =
                                    selectedOptions &&
                                    selectedOptions.length > 0
                                      ? selectedOptions[0]
                                      : null;
                                  setFieldValue(
                                    "trainerId",
                                    selected?.value || "",
                                  );
                                  setSelectedTrainerObj(selected || null);
                                }}
                                placeholder="Select trainer..."
                                isDisabled={!isFieldEditable("trainerId")}
                                isLoading={trainerPicker.isLoading}
                                onMenuOpen={trainerPicker.onMenuOpen}
                                onInputChange={trainerPicker.onInputChange}
                                onMenuScrollToBottom={trainerPicker.onMenuScrollToBottom}
                                onRefresh={trainerPicker.refresh}
                                loadingMessage="Loading trainers..."
                                noOptionsMessage="No trainers available"
                                noSearchResultsMessage="No trainers found"
                                styles={{
                                  control: (provided) => ({
                                    ...provided,
                                    ...getDisabledStyles(
                                      !isFieldEditable("trainerId"),
                                    ),
                                    backgroundColor: !isFieldEditable(
                                      "trainerId",
                                    )
                                      ? "#e9ecef"
                                      : provided.backgroundColor,
                                  }),
                                }}
                              />
                              {touched.trainerId && errors.trainerId && (
                                <div
                                  className="text-danger"
                                >
                                  {errors.trainerId}
                                </div>
                              )}
                            </div>
                          </Col>
                          <Col md={6}>
                            <div style={{ height: "100%" }}>
                              <label
                                className="form-label"
                              >
                                Classroom *
                              </label>
                              <NewCustomAsyncSelectDropdown
                                isMulti={false}
                                options={classroomPicker.options}
                                value={
                                  classroomPicker.options.find(
                                    (c) => c.value === values.classroom,
                                  )
                                    ? [
                                        classroomPicker.options.find(
                                          (c) => c.value === values.classroom,
                                        ),
                                      ]
                                    : []
                                }
                                onChange={(selectedOptions) => {
                                  const selected =
                                    selectedOptions &&
                                    selectedOptions.length > 0
                                      ? selectedOptions[0]
                                      : null;
                                  setFieldValue(
                                    "classroom",
                                    selected?.value || "",
                                  );
                                  setSelectedClassroomObj(selected || null);
                                  // A real room suggests its branch; the branch
                                  // is saved on this batch only, never on the room.
                                  const room = selected?.original;
                                  const roomBranch = room?.branch;
                                  if (roomBranch?.id && !isUnassignedRoom(room)) {
                                    setFieldValue("branch", roomBranch.id);
                                    setSelectedBranchObj({
                                      value: roomBranch.id,
                                      label: branchLabel(roomBranch),
                                      original: roomBranch,
                                    });
                                  }
                                }}
                                placeholder="Select classroom..."
                                isDisabled={!isFieldEditable("classroom")}
                                isLoading={classroomPicker.isLoading}
                                onMenuOpen={classroomPicker.onMenuOpen}
                                onInputChange={classroomPicker.onInputChange}
                                onMenuScrollToBottom={classroomPicker.onMenuScrollToBottom}
                                onRefresh={classroomPicker.refresh}
                                loadingMessage="Loading classrooms..."
                                noOptionsMessage="No classrooms available"
                                noSearchResultsMessage="No classrooms found"
                                styles={{
                                  control: (provided) => ({
                                    ...provided,
                                    ...getDisabledStyles(
                                      !isFieldEditable("classroom"),
                                    ),
                                    backgroundColor: !isFieldEditable(
                                      "classroom",
                                    )
                                      ? "#e9ecef"
                                      : provided.backgroundColor,
                                  }),
                                }}
                              />
                              {touched.classroom && errors.classroom && (
                                <div
                                  className="text-danger"
                                >
                                  {errors.classroom}
                                </div>
                              )}
                            </div>
                          </Col>
                        </Row>
                        <Row>
                          <Col md={6}>
                            <div style={{ height: "100%" }}>
                              <label
                                className="form-label"
                              >
                                Branch
                              </label>
                              <NewCustomAsyncSelectDropdown
                                isMulti={false}
                                options={branchPicker.options}
                                value={
                                  branchPicker.options.find(
                                    (b) => b.value === values.branch,
                                  )
                                    ? [
                                        branchPicker.options.find(
                                          (b) => b.value === values.branch,
                                        ),
                                      ]
                                    : []
                                }
                                onChange={(selectedOptions) => {
                                  const selected =
                                    selectedOptions &&
                                    selectedOptions.length > 0
                                      ? selectedOptions[0]
                                      : null;
                                  setFieldValue(
                                    "branch",
                                    selected?.value || "",
                                  );
                                  setSelectedBranchObj(selected || null);
                                }}
                                placeholder="Select branch..."
                                isDisabled={!isFieldEditable("branch")}
                                isLoading={branchPicker.isLoading}
                                onMenuOpen={branchPicker.onMenuOpen}
                                onInputChange={branchPicker.onInputChange}
                                onMenuScrollToBottom={branchPicker.onMenuScrollToBottom}
                                onRefresh={branchPicker.refresh}
                                loadingMessage="Loading branches..."
                                noOptionsMessage="No branches available"
                                noSearchResultsMessage="No branches found"
                                styles={{
                                  control: (provided) => ({
                                    ...provided,
                                    ...getDisabledStyles(!isFieldEditable("branch")),
                                    backgroundColor:
                                      !isFieldEditable("branch")
                                        ? "#e9ecef"
                                        : provided.backgroundColor,
                                  }),
                                }}
                              />
                            </div>
                          </Col>
                        </Row>
                      </div>

                      {/* Schedule Information Section - Only show for Admin or Create Mode */}

                      <div className="bf-section">
                        <h5 className="bf-section-title">
                          Schedule Information
                        </h5>
                        <Row>
                          <Col md={6}>
                            <label
                              className="form-label"
                            >
                              Start Time *
                            </label>
                            <DatePickerInput
                              name="startTime"
                              showTimeSelect
                              dateFormat="Pp"
                              placeholderText="Select start time"
                              disabled={!isFieldEditable("startTime")}
                              style={getDisabledStyles(
                                !isFieldEditable("startTime"),
                              )}
                            />
                          </Col>
                          <Col md={6}>
                            <label
                              className="form-label"
                            >
                              End Time *
                            </label>
                            <DatePickerInput
                              name="endTime"
                              showTimeSelect
                              dateFormat="Pp"
                              placeholderText="Select end time"
                              minDate={
                                values.startTime
                                  ? new Date(values.startTime)
                                  : null
                              }
                              disabled={!isFieldEditable("endTime")}
                              style={getDisabledStyles(
                                !isFieldEditable("endTime"),
                              )}
                            />
                          </Col>
                        </Row>
                        <Row>
                          <Col md={6}>
                            <label
                              className="form-label"
                            >
                              Select Days *
                            </label>
                            <div className="bf-selectall">
                              <div className="form-check">
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  id="selectAllDays"
                                  checked={
                                    values.days.length === dayOptions.length
                                  }
                                  onChange={(e) =>
                                    setFieldValue(
                                      "days",
                                      e.target.checked ? [...dayOptions] : [],
                                    )
                                  }
                                  disabled={!isFieldEditable("days")}
                                  style={getDisabledStyles(
                                    !isFieldEditable("days"),
                                  )}
                                />
                                <label
                                  className="form-check-label"
                                  htmlFor="selectAllDays"
                                  style={{ fontWeight: "500" }}
                                >
                                  Select All Days
                                </label>
                              </div>
                            </div>
                            <NewCustomAsyncSelectDropdown
                              isMulti
                              options={dayOptions}
                              value={values.days}
                              onChange={(val) =>
                                setFieldValue("days", val || [])
                              }
                              placeholder="Select days..."
                              isDisabled={!isFieldEditable("days")}
                              isSearchable={false}
                              styles={{
                                control: (provided) => ({
                                  ...provided,
                                  ...getDisabledStyles(
                                    !isFieldEditable("days"),
                                  ),
                                  backgroundColor: !isFieldEditable("days")
                                    ? "#e9ecef"
                                    : provided.backgroundColor,
                                }),
                              }}
                            />
                            {touched.days && errors.days && (
                              <div
                                className="text-danger"
                              >
                                {errors.days}
                              </div>
                            )}
                          </Col>
                        </Row>
                      </div>

                      {/* One note to add with this save. The API stamps it
                          with who and when; earlier notes are in the batch
                          view's Batch Notes tab and are never overwritten. */}
                      <div className="bf-section">
                        <h5 className="bf-section-title">Notes</h5>
                        <Row>
                          <Col md={12}>
                            <TextInput
                              as="textarea"
                              rows={3}
                              maxLength={2000}
                              name="newNote"
                              label={isEditMode ? "Add a note" : "Note (optional)"}
                              placeholder="Anything worth recording about this batch..."
                              disabled={!isFieldEditable("newNote")}
                              style={{
                                borderColor: "#e5e7eb",
                                borderRadius: "8px",
                                resize: "vertical",
                                ...getDisabledStyles(!isFieldEditable("newNote")),
                              }}
                            />
                          </Col>
                        </Row>
                      </div>
                      </section>
                      )}

                      {shownStep === 2 && (
                      <section className="att-step">
                      {/* Meeting Information Section - Editable by both Admin and Trainer (in edit mode) */}
                      <div className="bf-section">
                        <h5 className="bf-section-title">
                          Meeting Information
                        </h5>
                        <Row>
                          <Col md={6}>
                            <label
                              className="form-label"
                            >
                              Mode *
                            </label>
                            <NewCustomAsyncSelectDropdown
                              isMulti={false}
                              options={[
                                { value: "Online", label: "Online" },
                                { value: "Offline", label: "Offline" },
                                { value: "Hybrid", label: "Hybrid" },
                              ]}
                              value={
                                values.mode
                                  ? [{ value: values.mode, label: values.mode }]
                                  : []
                              }
                              onChange={(selectedOptions) => {
                                const selected =
                                  selectedOptions && selectedOptions.length > 0
                                    ? selectedOptions[0]
                                    : null;
                                setFieldValue("mode", selected?.value || "");
                              }}
                              placeholder="Select mode..."
                              isDisabled={!isFieldEditable("mode")}
                              isSearchable={false}
                              styles={{
                                control: (provided) => ({
                                  ...provided,
                                  ...getDisabledStyles(
                                    !isFieldEditable("mode"),
                                  ),
                                  backgroundColor: !isFieldEditable("mode")
                                    ? "#e9ecef"
                                    : provided.backgroundColor,
                                }),
                              }}
                            />
                            {touched.mode && errors.mode && (
                              <div
                                className="text-danger"
                              >
                                {errors.mode}
                              </div>
                            )}
                          </Col>
                          <Col md={6}>
                            <TextInput
                              name="meetingLink"
                              label="Meeting Link *"
                              placeholder="Zoom or Google Meet link"
                              disabled={!isFieldEditable("meetingLink")}
                              style={{
                                borderColor:
                                  touched.meetingLink && errors.meetingLink
                                    ? "#dc3545"
                                    : "#e5e7eb",
                                borderRadius: "8px",
                                ...getDisabledStyles(
                                  !isFieldEditable("meetingLink"),
                                ),
                              }}
                            />
                          </Col>
                        </Row>
                        {values.meetingLink && (
                          <Row>
                            <Col md={6}>
                              <TextInput
                                name="meetingId"
                                label="Meeting ID"
                                placeholder="Enter meeting ID"
                                disabled={!isFieldEditable("meetingId")}
                                style={getDisabledStyles(
                                  !isFieldEditable("meetingId"),
                                )}
                              />
                              {touched.meetingId && errors.meetingId && (
                                <div
                                  className="text-danger"
                                >
                                  {errors.meetingId}
                                </div>
                              )}
                            </Col>
                            <Col md={6}>
                              <TextInput
                                name="password"
                                label="Password"
                                placeholder="Enter password"
                                disabled={!isFieldEditable("password")}
                                style={getDisabledStyles(
                                  !isFieldEditable("password"),
                                )}
                              />
                              {touched.password && errors.password && (
                                <div
                                  className="text-danger"
                                >
                                  {errors.password}
                                </div>
                              )}
                            </Col>
                          </Row>
                        )}
                      </div>

                      {/* Study Material Section - Editable by both Admin and Trainer (in edit mode) */}
                      <div className="bf-section">
                        <h5 className="bf-section-title">
                          Study Material
                        </h5>
                        <Row>
                          <Col md={6}>
                            <TextInput
                              name="studyMaterialLink"
                              label="Study Materials Drive Link"
                              placeholder="Enter Google Drive link (optional)"
                              disabled={!isFieldEditable("studyMaterialLink")}
                              style={getDisabledStyles(
                                !isFieldEditable("studyMaterialLink"),
                              )}
                            />
                            {touched.studyMaterialLink &&
                              errors.studyMaterialLink && (
                                <div
                                  className="text-danger"
                                >
                                  {errors.studyMaterialLink}
                                </div>
                              )}
                          </Col>
                          <Col md={6}>
                            <TextInput
                              name="recordedLectureLink"
                              label="Recorded Lectures Drive Link"
                              placeholder="Enter Google Drive link (optional)"
                              disabled={!isFieldEditable("recordedLectureLink")}
                              style={getDisabledStyles(
                                !isFieldEditable("recordedLectureLink"),
                              )}
                            />
                            {touched.recordedLectureLink &&
                              errors.recordedLectureLink && (
                                <div
                                  className="text-danger"
                                >
                                  {errors.recordedLectureLink}
                                </div>
                              )}
                          </Col>
                        </Row>
                      </div>
                      </section>
                      )}

                      {shownStep === 3 && (
                      <section className="att-step">
                      {/* Student Assignment Section - Only show for Admin or Create Mode */}

                      <div className="bf-section">
                        <h5 className="bf-section-title">
                          Student Assignment
                        </h5>
                        <Row>
                          <Col md={12}>
                            <label
                              className="form-label"
                            >
                              Select Students *
                            </label>
                            {/* `d-flex align-items-center gap-2` around a
                                `flex-grow-1` wrapper -- four Bootstrap
                                utilities that resolve to nothing here, so
                                the refresh button dropped below a
                                full-width select. */}
                            <div className="bf-inline">
                              <div className="bf-inline-grow">
                                <AsyncSelectDropdown
                                  key={studentPickerKey}
                                  isMulti
                                  value={values.userId || []}
                                  loadOptions={loadStudentPage}
                                  pageSize={20}
                                  onChange={(newValue, actionMeta) => {
                                    if (
                                      actionMeta?.action === "remove-value" ||
                                      actionMeta?.action === "pop-value"
                                    ) {
                                      const removedStudent =
                                        actionMeta.removedValue;
                                      confirm({
                                        title: "Remove Student?",
                                        html: `Are you sure you want to remove <strong>${removedStudent.label.split(" - ")[0]}</strong> from this batch?`,
                                        tone: 'danger',
                                        confirmButtonText: "Yes, remove",
                                        cancelButtonText: "Cancel",
                                      }).then((result) => {
                                        if (result.isConfirmed) {
                                          setFieldValue(
                                            "userId",
                                            newValue || [],
                                          );
                                          setSelectedStudentObjs(
                                            newValue || [],
                                          );
                                          // Only the form field changed -- nothing is saved yet, so this
                                          // stays a toast rather than the popup's done panel.
                                          toast.success("Student has been removed from the batch.", { toast: true });
                                        }
                                      });
                                    } else {
                                      setFieldValue("userId", newValue || []);
                                      setSelectedStudentObjs(newValue || []);
                                    }
                                  }}
                                  placeholder="Select students..."
                                  isDisabled={!isFieldEditable("userId")}
                                  formatOptionLabel={(option) => (
                                    <div>
                                      <div className="bf-opt-name">
                                        {option.label.split(" - ")[0]}
                                      </div>
                                      <small className="bf-opt-meta">
                                        {option.label.split(" - ")[1]}
                                      </small>
                                    </div>
                                  )}
                                />
                              </div>
                              <button
                                type="button"
                                className="pg-icon-btn"
                                onClick={refreshStudents}
                                title="Refresh students"
                                aria-label="Refresh students"
                                disabled={!isFieldEditable("userId")}
                              >
                                <RefreshCw size={15} />
                              </button>
                            </div>
                            {touched.userId && errors.userId && (
                              <div
                                className="text-danger"
                              >
                                {errors.userId}
                              </div>
                            )}
                          </Col>
                        </Row>
                      </div>

                      </section>
                      )}
                      </div>
                      </div>

                      {/* ── Actions ──────────────────────────────────────
                          The attendance form's row: Back from step 2 on,
                          Cancel on step 1, Continue until the last step,
                          then the save. None of them is type="submit" --
                          the save runs through submitFromLastStep so a
                          failure on an earlier step can go back to it. */}
                      {!(isTrainer && !isAdmin) && (
                        <LabClashNote batchId={id} isEditMode={isEditMode} onChange={setLabClash} />
                      )}
                      <div className="att-actions">
                        {step > 1 ? (
                          <button
                            type="button"
                            className="pg-btn"
                            onClick={() => setStep(step - 1)}
                          >
                            Back
                          </button>
                        ) : onCancel ? (
                          <button
                            type="button"
                            className="pg-btn"
                            onClick={onCancel}
                          >
                            Cancel
                          </button>
                        ) : (
                          <Link
                            className="pg-btn"
                            to={
                              isTrainer && !isAdmin
                                ? "/trainer-calendar"
                                : "/batches"
                            }
                          >
                            Cancel
                          </Link>
                        )}
                        {step < STEP_LABELS.length ? (
                          <button
                            type="button"
                            className="pg-btn pg-btn--main"
                            onClick={() =>
                              goToNextStep({ validateForm, setTouched, touched })
                            }
                          >
                            Continue
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="pg-btn pg-btn--main"
                            disabled={
                              isSubmitting ||
                              labClash ||
                              (isTrainer && !isAdmin && !isEditMode)
                            }
                            onClick={() =>
                              submitFromLastStep({ validateForm, setTouched, submitForm })
                            }
                          >
                            {isSubmitting
                              ? isEditMode
                                ? "Updating..."
                                : "Adding..."
                              : isEditMode
                                ? "Update batch"
                                : "Add batch"}
                          </button>
                        )}
                      </div>
                    </Form>
                  )}
                </Formik>
              </div>
      </div>
    </div>
  );
};

export default AddBatch;
